import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { ProblemAddressResponse } from '@/core/generated/api';
import { problemAddressService } from '@/entities/ProblemAddress/api/problemAddressService';
import { AddressPointEditor } from '@/features/verifyAddressPoint/ui/AddressPointEditor';
import { useProblemAddressStore } from '@/entities/ProblemAddress/model/store';
import { RequestStatus } from '@/shared/model/requestStatus';
import { BackButton } from '@/shared/ui/BackButton/BackButton';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { EntityActionPanel } from '@/shared/ui/EntityActionPanel/EntityActionPanel';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import classes from './ProblemAddressesPage.module.scss';

/** Показывает координаты дома и результаты ручной проверки */
export const ProblemAddressDetailsPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const point = useProblemAddressStore((state) => state.current);
    const error = useProblemAddressStore((state) => state.detailsError);
    const status = useProblemAddressStore((state) => state.detailsStatus);
    const fetchDetails = useProblemAddressStore((state) => state.fetchDetails);
    const setPoint = useProblemAddressStore((state) => state.setCurrent);

    const refresh = useCallback(() => {
        if (id) void fetchDetails(id);
    }, [id, fetchDetails]);

    useEffect(() => {
        if (!id) return;

        const controller = new AbortController();

        void fetchDetails(id, controller.signal);

        return () => controller.abort();
    }, [id, fetchDetails]);

    /** Применяет сохранённые координаты и открывает следующий дом */
    const saved = async (updated: ProblemAddressResponse, openNext: boolean) => {
        if (!openNext) {
            setPoint(updated);

            return;
        }

        try {
            const next = await problemAddressService.getList({
                isVerified: false,
                takeCount: 1,
                ascending: true
            });

            const nextId = next.addresses?.[0]?.id;

            navigate(nextId
                ? `/problem-addresses/${nextId}`
                : '/problem-addresses');
        } catch {
            setPoint(updated);
            toast.info('Координаты сохранены. Не удалось открыть следующий адрес');
        }
    };

    if (!id) return <RequestEmpty message="Не указан адрес" />;
    if (status === RequestStatus.Error) return <RequestError
        message={error ?? 'Не удалось загрузить адрес'}
        onRetry={refresh}
    />;
    if (status === RequestStatus.Loading || status === RequestStatus.Idle) return <RequestLoading message="Загрузка координат дома..." />;
    if (!point || point.id !== id) return <RequestEmpty message="Адрес не найден" />;

    return <div className={classes.page}>
        <EntityActionPanel name={[point.city, point.street, point.houseNumber].filter(Boolean).join(', ')}>
            <div className={classes.toolbar}>
                <BackButton url="/problem-addresses" />
                <ActionButton
                    label="Обновить карточку"
                    icon="pi pi-refresh"
                    onClick={refresh}
                />
            </div>
        </EntityActionPanel>
        <AddressPointEditor
            key={`${point.id}:${point.version}`}
            point={point}
            onSaved={saved}
        />
    </div>;
};
